import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, Check, Loader2, Zap, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner } from "@/pages/_shared";

const SALES_EMAIL = "sales@bdvgai.com";

export default function Billing() {
  const { current, refresh, can, subscription, openPortal } = useOrg();
  const [plans, setPlans] = useState(null);
  const [interval, setInterval] = useState("month");
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => setPlans([]));
    const sid = params.get("session_id");
    if (sid) {
      api.get(`/billing/status/${sid}`).then(({ data }) => {
        if (data.payment_status === "paid") { toast.success("Subscription active!"); refresh(); }
      }).finally(() => setParams({}));
    }
    if (params.get("canceled")) { toast("Checkout canceled"); setParams({}); }
    // eslint-disable-next-line
  }, []);

  const isActive = subscription?.subscription_active;
  const comp = subscription?.comp_type;

  const checkout = async (plan) => {
    if (plan.contact_sales) { window.location.href = `mailto:${SALES_EMAIL}?subject=BDVG Enterprise enquiry`; return; }
    if (!plan.stripe_available) { toast("This plan isn't available for online purchase yet — contact us to get set up."); return; }
    if (isActive) { return manage(); }
    setBusy(plan.code);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        plan: plan.code.replace("diamond_", ""),
        billing_period: interval === "year" ? "annual" : "monthly",
        origin_url: window.location.origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
    finally { setBusy(""); }
  };
  const manage = async () => {
    setBusy("portal");
    try { await openPortal(); }
    catch (e) { toast.error(e.response?.data?.detail || "Billing portal unavailable"); }
    finally { setBusy(""); }
  };
  const cancel = async () => { if (!window.confirm("Cancel subscription? You keep access until period end; data is retained.")) return; await api.post("/billing/cancel"); toast.success("Cancellation scheduled"); refresh(); };
  const reactivate = async () => { await api.post("/billing/reactivate"); toast.success("Subscription reactivated"); refresh(); };

  if (!plans) return <Spinner />;
  const org = current?.org;
  const currentPlanId = org?.plan_id;

  return (
    <Scroller>
      <PageHeader title="Billing & Plans" subtitle="Manage your BDVG AI subscription" icon={CreditCard} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gold/20 bg-gold/[0.04] p-5">
        <div>
          <p className="text-sm text-white/50">Current status</p>
          <p className="font-heading text-xl capitalize text-gradient-gold" data-testid="billing-status">{comp ? subscription.label : `${org?.subscription_status}${org?.billing_period ? ` · ${org.billing_period}` : ""}`}</p>
          {comp && (
            <p className="mt-1 text-xs text-white/45" data-testid="comp-note">Complimentary internal access — no subscription or card required{subscription?.minor ? " · guardian-managed account" : ""}.</p>
          )}
          {!comp && subscription?.trialing && subscription?.trial_days_left != null && (
            <p className="mt-1 text-xs text-white/45">Free trial · {subscription.trial_days_left} {subscription.trial_days_left === 1 ? "day" : "days"} left{org?.trial_ends_at ? ` (ends ${new Date(org.trial_ends_at).toLocaleDateString()})` : ""}</p>
          )}
          {!comp && org?.current_period_end && isActive && (
            <p className="mt-1 text-xs text-white/45">Renews {new Date(org.current_period_end).toLocaleDateString()}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {isActive && !comp && can("billing.manage") && (
            <button onClick={manage} disabled={busy === "portal"} className="flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-sm font-semibold text-gold hover:bg-gold/10 disabled:opacity-40" data-testid="billing-portal">
              {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings2 className="h-4 w-4" />} Manage subscription
            </button>
          )}
          <div className="flex items-center gap-2 rounded-full border border-white/10 p-1 text-xs">
            <button onClick={() => setInterval("month")} className={`rounded-full px-3 py-1.5 ${interval === "month" ? "bg-gold text-black" : "text-white/60"}`} data-testid="billing-toggle-monthly">Monthly</button>
            <button onClick={() => setInterval("year")} className={`rounded-full px-3 py-1.5 ${interval === "year" ? "bg-gold text-black" : "text-white/60"}`} data-testid="billing-toggle-annual">Annual</button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {plans.map((p) => {
          const price = interval === "year" ? p.price_annual : p.price_monthly;
          const isCurrent = p.id === currentPlanId;
          return (
            <div key={p.id} className={`flex flex-col rounded-2xl border p-6 ${isCurrent ? "border-gold/50 shadow-gold-sm" : "border-white/8 bg-charcoal"}`} data-testid={`plan-${p.code}`}>
              <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-gold" /><h3 className="font-heading text-lg font-semibold">{p.name}</h3></div>
              <p className="mt-1 text-sm text-white/45">{p.description}</p>
              <p className="mt-4 font-heading text-3xl font-bold">{p.contact_sales ? "Custom" : `£${price}`}<span className="text-sm font-normal text-white/40">{p.contact_sales ? "" : `/${interval === "year" ? "yr" : "mo"}`}</span></p>
              <ul className="mt-4 flex-1 space-y-1.5 text-sm text-white/60">
                <li>· {p.limits.companies === -1 ? "Unlimited" : p.limits.companies} company(ies)</li>
                <li>· {p.limits.users === -1 ? "Custom" : p.limits.users} users</li>
                <li>· {p.limits.ai_requests === -1 ? "Custom" : p.limits.ai_requests.toLocaleString()} AI requests/mo</li>
                <li>· {p.modules === -1 ? "All modules" : `${p.modules} modules`}</li>
                {p.contact_sales && <li>· Custom integrations & priority support</li>}
              </ul>
              {comp ? (
                <div className="mt-5 rounded-xl border border-gold/20 py-2.5 text-center text-sm text-gold/70" data-testid={`plan-comp-${p.code}`}>Included</div>
              ) : p.contact_sales ? (
                <button onClick={() => checkout(p)} className="mt-5 rounded-xl border border-gold/30 py-2.5 text-center text-sm font-semibold text-gold hover:bg-gold/10" data-testid={`plan-select-${p.code}`}>Contact Sales</button>
              ) : isCurrent ? (
                <div className="mt-5 flex items-center justify-center gap-2 rounded-xl border border-gold/30 py-2.5 text-sm text-gold"><Check className="h-4 w-4" /> Current plan</div>
              ) : !can("billing.manage") ? (
                <div className="mt-5 rounded-xl border border-white/10 py-2.5 text-center text-sm text-white/40">Ask an admin</div>
              ) : isActive ? (
                <button onClick={manage} disabled={busy === "portal"} className="mt-5 rounded-xl border border-white/15 py-2.5 text-center text-sm font-semibold text-white/80 hover:border-gold/40 hover:text-gold disabled:opacity-40" data-testid={`plan-select-${p.code}`}>Change to this plan</button>
              ) : p.stripe_available ? (
                <button onClick={() => checkout(p)} disabled={busy === p.code} className="mt-5 flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-gold-light to-gold py-2.5 text-sm font-semibold text-black disabled:opacity-40" data-testid={`plan-select-${p.code}`}>
                  {busy === p.code ? <Loader2 className="h-4 w-4 animate-spin" /> : "Choose plan"}
                </button>
              ) : (
                <div className="mt-5 rounded-xl border border-white/10 py-2.5 text-center text-sm text-white/40">Coming soon</div>
              )}
            </div>
          );
        })}
      </div>

      {can("billing.manage") && org?.subscription_status === "active" && (
        <button onClick={cancel} className="mt-6 text-sm text-white/40 hover:text-destructive" data-testid="billing-cancel">Cancel subscription</button>
      )}
      {can("billing.manage") && org?.subscription_status === "canceled" && org?.stripe_subscription_id && (
        <button onClick={reactivate} className="mt-6 ml-4 text-sm text-gold hover:underline" data-testid="billing-reactivate">Reactivate subscription</button>
      )}
      <p className="mt-6 text-xs text-white/30">Payments are handled securely by Stripe — we never store your card details.</p>
    </Scroller>
  );
}
